// src/components/WorldBuildingView.tsx
import React, { useState, useContext, useEffect } from "react"; // Added useEffect
import { LoadingStates } from '../types'; 
import { labelClass, inputClass, textareaClass, primaryButtonClass, secondaryButtonClass, dangerButtonClass, actionButtonClass } from '../constants';
import { useWorldBuildingManager } from '../hooks/useWorldBuildingManager'; 
import { AppContext, AppContextType } from '../contexts/AppContext'; // Import AppContext
import type { BookMetadata, WorldBuildingData } from '../types'; // Import necessary types
import { getProjectTitle, syncSelectedStoryTitleWithDB } from '../utils/projectUtils'; // Import helper functions
import { useToast } from '../../hooks/use-toast';
import { 
    useViewEditMode, 
    ViewEditSection, 
    SaveCancelButtons, 
    DisplayField, 
    DisplayGrid,
    formatDisplayValue 
} from '../utils/viewEditUtils';
import { useOptimizedSave } from '../hooks/useOptimizedSave';

interface WorldBuildingViewProps {
    // BLUEPRINT MIGRATION: Use blueprint.worldBuildingSuggestions as single source of truth
    blueprint: { bookMetadata: BookMetadata; worldBuildingSuggestions: WorldBuildingData; };
    onBlueprintChange: (section: 'bookMetadata' | 'worldBuildingSuggestions', field: keyof WorldBuildingData | keyof BookMetadata | "all", value: any) => void;
    hasInitialBlueprint: boolean;
    outlineIdea: string;
    bookMetadata: BookMetadata;
    coreConcepts: { logLine: string, storyStartConcept: string, storyEndConcept: string };
}

const ChevronIcon: React.FC<{isExpanded: boolean}> = ({ isExpanded }) => (
    <svg className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
    </svg>
);


const WorldBuildingView: React.FC<WorldBuildingViewProps> = ({
    blueprint,
    onBlueprintChange,
    hasInitialBlueprint,
    outlineIdea,
    bookMetadata,
    coreConcepts,
}) => {
    // BLUEPRINT MIGRATION: Extract worldBuildingSuggestions from blueprint
    const worldBuildingData = blueprint?.worldBuildingSuggestions || {
        logLine: '',
        storyStartConcept: '',
        storyEndConcept: '',
        selectedStoryTitle: '',
        storyTitleIdeas: [],
        characters: [],
        locations: [],
        customCategories: [],
        themes: []
    };
    
    // BLUEPRINT MIGRATION: Convert blueprint change handler to worldBuildingData pattern
    const handleWorldBuildingDataChange = (field: keyof WorldBuildingData | "all", value: any) => {
        onBlueprintChange('worldBuildingSuggestions', field, value);
    };
    const appContext = useContext(AppContext);
    const { activeEditSection, handleEditToggle, handleSaveAndExit, handleCancelEdit } = useViewEditMode();
    const { toast } = useToast();
    const { saveWorldBuilding } = useOptimizedSave();
    
    const handleSaveChanges = async () => {
        try {
            // Get blueprint state management functions from optimized data context
            const { blueprint } = appContext?.optimizedData || {};
            
            await saveWorldBuilding.mutateAsync({
                projectId: appContext?.dbProject?.uuid || appContext?.dbProject?.id?.toString() || '',
                // BLUEPRINT MIGRATION: Save worldBuildingData to blueprint.worldBuildingSuggestions
                worldBuildingData,
                setBlueprintSaving: blueprint?.setBlueprintSaving
            });
            handleSaveAndExit();
        } catch (error) {
            console.error('Failed to save changes:', error);
            // Show error toast
            toast({
                title: "Save Failed",
                description: "Failed to save world building changes. Please try again.",
                variant: "destructive"
            });
        }
    }; 
    
    if (!appContext) {
        return <p>Loading World Building context...</p>; 
    }
    const { projectState: currentProjectStateFromContext, setError: setContextError, isLoading: contextIsLoading, dbProject } = appContext;

    // Sync selectedStoryTitle with database project title on component mount/project change
    useEffect(() => {
        if (dbProject?.title && dbProject.title !== worldBuildingData.selectedStoryTitle) {
            const syncedData = syncSelectedStoryTitleWithDB(worldBuildingData, dbProject);
            if (syncedData !== worldBuildingData) {
                handleWorldBuildingDataChange('selectedStoryTitle', dbProject.title);
            }
        }
    }, [dbProject?.title, worldBuildingData.selectedStoryTitle, handleWorldBuildingDataChange]);

    // BLUEPRINT MIGRATION: useWorldBuildingManager uses converted handler
    const {
        handleStoryTitleIdeasChange,
        handleSelectedStoryTitleChange,
        handleCharacterChange,
        handleAddCharacter,
        handleDeleteCharacter,
        handleRefineCharactersWithAI,
        handleDeleteAllCharacters, 
        handleLocationChange,
        handleAddLocation,
        handleDeleteLocation,
        handleRefineLocationsWithAI,
        handleDeleteAllLocations, 
        handleAddCustomCategory,
        handleCustomCategoryNameChange,
        handleDeleteCustomCategory,
        handleDeleteAllCustomCategories, 
        handleAddCustomItem,
        handleCustomItemChange,
        handleDeleteCustomItem,
        handleGenerateCustomCategoryItemsWithAI,
        handleRefineCustomCategoryItemsWithAI,
        handleRegenerateAllWorldBuildingData, 
    } = useWorldBuildingManager({
        worldBuildingData,
        onWorldBuildingDataChange: handleWorldBuildingDataChange,
        outlineIdea,
        bookMetadata,
        coreConcepts,
    });

    const [customTitleInput, setCustomTitleInput] = useState('');
    const [characterRefineText, setCharacterRefineText] = useState('');
    const [locationRefineText, setLocationRefineText] = useState('');
    
    const [customCategoryAIGenerateInputs, setCustomCategoryAIGenerateInputs] = useState<Record<string, string>>({});
    const [customCategoryAIRefineInputs, setCustomCategoryAIRefineInputs] = useState<Record<string, string>>({});
    const [overallRevisionPrompt, setOverallRevisionPrompt] = useState(''); 
    const [allowBlueprintRegeneration, setAllowBlueprintRegeneration] = useState(false); // New state for checkbox

    const [expandedItemIds, setExpandedItemIds] = useState<Record<string, boolean>>({});
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        characters: true,
        locations: true,
        customElements: true,
    });


    useEffect(() => {
        // This effect can be used for debugging if specific state changes need to be tracked
        // console.log("overallRevisionPrompt (local state) CHANGED to:", overallRevisionPrompt);
    }, [overallRevisionPrompt]);

    useEffect(() => {
        // This effect can be used for debugging if specific state changes need to be tracked
        // console.log("worldBuildingData (from hook) CHANGED:", JSON.parse(JSON.stringify(worldBuildingData)));
    }, [worldBuildingData]);


    const showPotentialRevisionNeededNote = currentProjectStateFromContext && 
                                           currentProjectStateFromContext.chapters.length > 0 && 
                                           (currentProjectStateFromContext.bookMetadataSnapshotForChapters !== null || 
                                            // FIX: Changed worldBuildingDataSnapshotForChapters to blueprintSnapshotForChapters
                                            currentProjectStateFromContext.blueprintSnapshotForChapters !== null);


    const toggleExpandedItem = (id: string) => {
        setExpandedItemIds(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleSectionExpanded = (sectionKey: string) => {
        setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    };

    const handleAddCustomTitleInternal = () => {
        if (customTitleInput.trim() && !worldBuildingData.storyTitleIdeas?.includes(customTitleInput.trim())) {
            handleStoryTitleIdeasChange([...worldBuildingData.storyTitleIdeas || [], customTitleInput.trim()]);
            handleSelectedStoryTitleChange(customTitleInput.trim()); 
            setCustomTitleInput('');
        }
    };

    const triggerRegenerateAll = () => {
        if (overallRevisionPrompt.trim()) {
            handleRegenerateAllWorldBuildingData(overallRevisionPrompt);
        } else {
            setContextError("Please provide a prompt to guide the regeneration of world data.");
        }
    };
    
    return (
        <div className="space-y-8 p-1 pb-16 h-full overflow-y-auto" style={{backgroundImage: "url('/assets/texture_tile.png')", backgroundRepeat: 'repeat', backgroundSize: 'auto'}}> 
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-3 mb-6">World Building & Core Concepts</h2>
            <div className="p-3 mb-4 text-xs text-red-900 bg-red-100 rounded-lg dark:bg-red-200 dark:text-blue-800" role="note">
                   <strong> Remember to run building entire world building components after changing any of the fields for best output 
                     </strong>
                </div>
            
            {!hasInitialBlueprint && (
                <div className="p-3 mb-4 text-sm text-yellow-700 bg-yellow-100 rounded-lg" role="alert">
                    Generate your initial blueprint first in the 'Idea Hub' to populate these details.
                </div>
            )}

            {hasInitialBlueprint && (
                <div className="flex items-center mb-4">
                    <input
                        type="checkbox"
                        id="allowBlueprintRegeneration"
                        checked={allowBlueprintRegeneration}
                        onChange={(e) => setAllowBlueprintRegeneration(e.target.checked)}
                        className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="allowBlueprintRegeneration" className="text-sm text-gray-700">
                        Allow regeneration of initial blueprint (this will overwrite existing data)
                    </label>
                </div>
            )}

            {showPotentialRevisionNeededNote && (
                <div className="p-3 mb-4 text-xs text-blue-700 bg-blue-100 rounded-lg dark:bg-blue-200 dark:text-blue-800" role="note">
                    Note: Chapter outlines currently exist. Changes made here may require you to revise outlines in 'Chapters Building'. 
                    <strong> Additionally, any manual edits to World Building elements should be incorporated into the 'Overall Regeneration/Revision Prompt' (at the bottom of this page) and run. This ensures the AI uses the most up-to-date world state for future generations (e.g., new chapter outlines or chapter content revisions).</strong>
                </div>
            )}

            {/* Story Vitals Section with View/Edit Mode */}
            <ViewEditSection
                title="Story Vitals"
                subtitle="Core story concepts and title selection"
                isActive={activeEditSection === 'vitals'}
                onEditToggle={() => handleEditToggle('vitals')}
            >
                {activeEditSection === 'vitals' ? (
                    <div className="space-y-4">
                        <div className="mb-6">
                            <label className={`${labelClass} mb-2`}>Story Title:</label>
                            {worldBuildingData.storyTitleIdeas?.map((title, index) => (
                                <div key={index} className="flex items-center mb-1">
                                    <input
                                        type="radio"
                                        id={`title-idea-${index}`}
                                        name="selectedStoryTitle"
                                        value={title}
                                        checked={worldBuildingData.selectedStoryTitle === title}
                                        onChange={(e) => handleSelectedStoryTitleChange(e.target.value)}
                                        className="mr-2 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                        disabled={!hasInitialBlueprint}
                                    />
                                    <label htmlFor={`title-idea-${index}`} className="text-sm text-gray-700">{title}</label>
                                </div>
                            ))}
                            <div className="flex items-center mt-2 space-x-2">
                                <input
                                    type="text"
                                    value={customTitleInput}
                                    onChange={(e) => setCustomTitleInput(e.target.value)}
                                    placeholder="Add a new title idea..."
                                    className={`${inputClass} flex-grow text-sm`}
                                    disabled={!hasInitialBlueprint}
                                />
                                <button onClick={handleAddCustomTitleInternal} className={`${secondaryButtonClass} text-xs px-3 py-1.5`} disabled={!hasInitialBlueprint}>Add & Select</button>
                            </div>
                             <p className="text-xs text-gray-500 mt-1">Select one of the AI-suggested titles or add your own. The selected title will be used.</p>
                        </div>
                        {(['logLine', 'storyStartConcept', 'storyEndConcept'] as const).map(field => (
                            <div key={field} className="mb-4">
                                <label htmlFor={field} className={labelClass}>
                                    {field === 'logLine' ? 'Logline/Pitch:' : field === 'storyStartConcept' ? 'Story Start Concept:' : 'Story End Concept:'}
                                </label>
                                <textarea
                                    id={field}
                                    value={worldBuildingData[field]}
                                    onChange={(e) => handleWorldBuildingDataChange(field, e.target.value)}
                                    placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}...`}
                                    className={`${textareaClass} min-h-[60px]`}
                                    rows={3}
                                    disabled={!hasInitialBlueprint}
                                />
                            </div>
                        ))}
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <DisplayField
                            label="Selected Story Title"
                            value={getProjectTitle(dbProject, worldBuildingData.selectedStoryTitle)}
                            background={true}
                        />
                        <DisplayField
                            label="Logline/Pitch"
                            value={worldBuildingData.logLine}
                            background={true}
                        />
                        <DisplayField
                            label="Story Start Concept"
                            value={worldBuildingData.storyStartConcept}
                            background={true}
                        />
                        <DisplayField
                            label="Story End Concept"
                            value={worldBuildingData.storyEndConcept}
                            background={true}
                        />
                        {worldBuildingData.storyTitleIdeas?.length > 0 && (
                            <div>
                                <h4 className="font-medium text-gray-700 mb-2">All Title Ideas:</h4>
                                <div className="flex flex-wrap gap-2">
                                    {worldBuildingData.storyTitleIdeas?.map((title, index) => (
                                        <span key={index} className={`px-2 py-1 text-xs rounded-full ${
                                            worldBuildingData.selectedStoryTitle === title 
                                                ? 'bg-blue-100 text-blue-800' 
                                                : 'bg-gray-100 text-gray-700'
                                        }`}>
                                            {title}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </ViewEditSection>

            {/* Characters Section with View/Edit Mode */}
            <ViewEditSection
                title="Characters"
                subtitle="Character profiles and development"
                isActive={activeEditSection === 'characters'}
                onEditToggle={() => handleEditToggle('characters')}
                headerActions={
                    activeEditSection !== 'characters' && worldBuildingData.characters.length > 0 ? (
                        <button 
                            onClick={handleDeleteAllCharacters}
                            className={`${dangerButtonClass} text-xs px-2 py-1`}
                            title="Delete all characters"
                            disabled={!hasInitialBlueprint}
                        >
                            Delete All Characters
                        </button>
                    ) : undefined
                }
            >
                {activeEditSection === 'characters' ? (
                    <div className="space-y-4">
                        {worldBuildingData.characters?.map((char) => (
                            <div key={char.id} className="mb-3 p-3 border rounded-md bg-gray-50">
                                <div className="flex justify-between items-center">
                                    <button
                                        onClick={() => toggleExpandedItem(char.id)}
                                        className="flex items-center text-left focus:outline-none py-1 flex-grow"
                                        aria-expanded={!!expandedItemIds[char.id]}
                                        aria-controls={`char-details-${char.id}`}
                                    >
                                        <ChevronIcon isExpanded={!!expandedItemIds[char.id]} />
                                        <span className="ml-2 font-medium text-gray-700">{char.name || `Character ${char.id}`}</span>
                                    </button>
                                    <button onClick={() => handleDeleteCharacter(worldBuildingData.characters.indexOf(char))} className={`${dangerButtonClass} text-xs px-2 py-1 ml-2`}>Delete</button>
                                </div>
                                {expandedItemIds[char.id] && (
                                    <div id={`char-details-${char.id}`} className="space-y-2 pl-7 pt-2 border-t border-gray-200 mt-2">
                                        <div>
                                            <label htmlFor={`char-name-${char.id}`} className={labelClass}>Name:</label>
                                            <input id={`char-name-${char.id}`} type="text" value={char.name} onChange={(e) => handleCharacterChange(worldBuildingData.characters.indexOf(char), 'name', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label htmlFor={`char-desc-${char.id}`} className={labelClass}>Brief Description:</label>
                                            <textarea id={`char-desc-${char.id}`} value={char.description} onChange={(e) => handleCharacterChange(worldBuildingData.characters.indexOf(char), 'description' as any, e.target.value)} className={`${textareaClass} min-h-[50px]`} rows={2} />
                                        </div>
                                        <div>
                                            <label htmlFor={`char-role-${char.id}`} className={labelClass}>Role:</label>
                                            <input id={`char-role-${char.id}`} type="text" value={char.role} onChange={(e) => handleCharacterChange(worldBuildingData.characters.indexOf(char), 'role' as any, e.target.value)} className={inputClass} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <button onClick={handleAddCharacter} className={`${primaryButtonClass} mt-2`} disabled={!hasInitialBlueprint}>Add New Character Manually</button>

                        <div className="mt-6 pt-4 border-t">
                            <h4 className="text-md font-semibold text-gray-600 mb-2">Refine/Expand Character List with AI:</h4>
                            <textarea 
                                value={characterRefineText}
                                onChange={(e) => setCharacterRefineText(e.target.value)}
                                placeholder="Paste new character ideas or modifications here. The AI will use this, existing characters, and project context."
                                className={`${textareaClass} min-h-[80px] mb-2`}
                                rows={3}
                                disabled={!hasInitialBlueprint}
                            />
                            <button 
                                onClick={() => { handleRefineCharactersWithAI(characterRefineText); setCharacterRefineText(''); }}
                                disabled={contextIsLoading.parseCharacters || !characterRefineText.trim() || !hasInitialBlueprint}
                                className={`${actionButtonClass} w-full`}
                            >
                                {contextIsLoading.parseCharacters ? 'Processing with AI...' : 'Refine/Expand Characters (AI)'}
                            </button>
                            <p className="text-xs text-gray-500 mt-1">The AI will update your character list based on your input and existing data.</p>
                        </div>
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {worldBuildingData.characters.length > 0 ? (
                            worldBuildingData.characters?.map((char, index) => (
                                <DisplayField
                                    key={char.id}
                                    label={`Character ${index + 1}: ${char.name || 'Unnamed'}`}
                                    value={`${char.role ? `${char.role} - ` : ''}${char.description}`}
                                    background={true}
                                />
                            ))
                        ) : (
                            <DisplayField
                                label="Characters"
                                value="No characters defined yet"
                                background={true}
                            />
                        )}
                    </div>
                )}
            </ViewEditSection>

            {/* Locations Section with View/Edit Mode */}
            <ViewEditSection
                title="Locations"
                subtitle="Story settings and environments"
                isActive={activeEditSection === 'locations'}
                onEditToggle={() => handleEditToggle('locations')}
                headerActions={
                    activeEditSection !== 'locations' && worldBuildingData.locations.length > 0 ? (
                        <button 
                            onClick={handleDeleteAllLocations}
                            className={`${dangerButtonClass} text-xs px-2 py-1`}
                            title="Delete all locations"
                            disabled={!hasInitialBlueprint}
                        >
                            Delete All Locations
                        </button>
                    ) : undefined
                }
            >
                {activeEditSection === 'locations' ? (
                    <div className="space-y-4">
                        {worldBuildingData.locations?.map((loc) => (
                            <div key={loc.id} className="mb-3 p-3 border rounded-md bg-gray-50">
                                <div className="flex justify-between items-center">
                                    <button
                                        onClick={() => toggleExpandedItem(loc.id)}
                                        className="flex items-center text-left focus:outline-none py-1 flex-grow"
                                        aria-expanded={!!expandedItemIds[loc.id]}
                                        aria-controls={`loc-details-${loc.id}`}
                                    >
                                        <ChevronIcon isExpanded={!!expandedItemIds[loc.id]} />
                                        <span className="ml-2 font-medium text-gray-700">{loc.name || `Location ${loc.id}`}</span>
                                    </button>
                                    <button onClick={() => handleDeleteLocation(worldBuildingData.locations.indexOf(loc))} className={`${dangerButtonClass} text-xs px-2 py-1 ml-2`}>Delete</button>
                                </div>
                                {expandedItemIds[loc.id] && (
                                    <div id={`loc-details-${loc.id}`} className="space-y-2 pl-7 pt-2 border-t border-gray-200 mt-2">
                                        <div>
                                            <label htmlFor={`loc-name-${loc.id}`} className={labelClass}>Name:</label>
                                            <input id={`loc-name-${loc.id}`} type="text" value={loc.name} onChange={(e) => handleLocationChange(worldBuildingData.locations.indexOf(loc), 'name', e.target.value)} className={inputClass} />
                                        </div>
                                        <div>
                                            <label htmlFor={`loc-desc-${loc.id}`} className={labelClass}>Description (Atmosphere, Significance, Nature, Appearance, etc.):</label>
                                            <textarea id={`loc-desc-${loc.id}`} value={loc.briefDescription} onChange={(e) => handleLocationChange(worldBuildingData.locations.indexOf(loc), 'briefDescription', e.target.value)} className={`${textareaClass} min-h-[50px]`} rows={2} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <button onClick={handleAddLocation} className={`${primaryButtonClass} mt-2`} disabled={!hasInitialBlueprint}>Add New Location Manually</button>

                        <div className="mt-6 pt-4 border-t">
                            <h4 className="text-md font-semibold text-gray-600 mb-2">Refine/Expand Location List with AI:</h4>
                            <textarea 
                                value={locationRefineText}
                                onChange={(e) => setLocationRefineText(e.target.value)}
                                placeholder="Paste new location ideas or modifications here. The AI will use this, existing locations, and project context."
                                className={`${textareaClass} min-h-[80px] mb-2`}
                                rows={3}
                                disabled={!hasInitialBlueprint}
                            />
                            <button 
                                onClick={() => { handleRefineLocationsWithAI(locationRefineText); setLocationRefineText(''); }}
                                disabled={contextIsLoading.parseLocations || !locationRefineText.trim() || !hasInitialBlueprint}
                                className={`${actionButtonClass} w-full`}
                            >
                                {contextIsLoading.parseLocations ? 'Processing with AI...' : 'Refine/Expand Locations (AI)'}
                            </button>
                            <p className="text-xs text-gray-500 mt-1">The AI will update your location list based on your input and existing data.</p>
                        </div>
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {worldBuildingData.locations.length > 0 ? (
                            worldBuildingData.locations?.map((loc, index) => (
                                <DisplayField
                                    key={loc.id}
                                    label={`Location ${index + 1}: ${loc.name || 'Unnamed'}`}
                                    value={loc.briefDescription}
                                    background={true}
                                />
                            ))
                        ) : (
                            <DisplayField
                                label="Locations"
                                value="No locations defined yet"
                                background={true}
                            />
                        )}
                    </div>
                )}
            </ViewEditSection>

            {/* Custom World Elements Section with View/Edit Mode */}
            <ViewEditSection
                title="Custom World Elements"
                subtitle="Unique aspects like magic systems, technologies, factions, events"
                isActive={activeEditSection === 'customElements'}
                onEditToggle={() => handleEditToggle('customElements')}
                headerActions={
                    activeEditSection !== 'customElements' && worldBuildingData.customCategories.length > 0 ? (
                        <button 
                            onClick={handleDeleteAllCustomCategories}
                            className={`${dangerButtonClass} text-xs px-2 py-1`}
                            title="Delete all custom categories and their items"
                            disabled={!hasInitialBlueprint}
                        >
                            Delete All Custom Categories
                        </button>
                    ) : undefined
                }
            >
                {activeEditSection === 'customElements' ? (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 mb-4">Define unique aspects of your world like magic systems, technologies, factions, historical events, etc.</p>
                        {worldBuildingData.customCategories?.map((category) => (
                            <div key={category.id} className="mb-6 p-4 border-2 border-gray-200 rounded-lg bg-gray-50">
                                <div className="flex justify-between items-center mb-3">
                                    <input 
                                        type="text" 
                                        value={category.categoryName} 
                                        onChange={(e) => handleCustomCategoryNameChange(worldBuildingData.customCategories.indexOf(category), e.target.value)}
                                        placeholder="Category Name (e.g., Magic Systems)"
                                        className={`${inputClass} font-semibold text-lg text-gray-700`}
                                    />
                                    <button onClick={() => handleDeleteCustomCategory(worldBuildingData.customCategories.indexOf(category))} className={`${dangerButtonClass} text-xs px-2 py-1 ml-2`}>Delete Category</button>
                                </div>
                                {category.items?.map((item) => (
                                    <div key={item.id} className="ml-1 mb-2 p-3 border border-gray-300 rounded-md bg-white shadow">
                                        <div className="flex justify-between items-center">
                                            <button
                                                onClick={() => toggleExpandedItem(item.id)}
                                                className="flex items-center text-left focus:outline-none py-1 flex-grow"
                                                aria-expanded={!!expandedItemIds[item.id]}
                                                aria-controls={`custom-item-details-${item.id}`}
                                            >
                                                <ChevronIcon isExpanded={!!expandedItemIds[item.id]} />
                                                <span className="ml-1.5 font-medium text-sm text-gray-700">{item.name || `Item ${item.id}`}</span>
                                            </button>
                                            <button onClick={() => handleDeleteCustomItem(worldBuildingData.customCategories.indexOf(category), category.items.indexOf(item))} className={`${dangerButtonClass} text-xs px-1.5 py-0.5 ml-2`}>Delete Item</button>
                                        </div>
                                        {expandedItemIds[item.id] && (
                                            <div id={`custom-item-details-${item.id}`} className="space-y-1 pl-7 pt-2 border-t border-gray-200 mt-2">
                                                <div>
                                                    <label htmlFor={`cat-${category.id}-item-name-${item.id}`} className={`${labelClass} text-xs`}>Name/Concept:</label>
                                                    <input id={`cat-${category.id}-item-name-${item.id}`} type="text" value={item.name} onChange={(e) => handleCustomItemChange(worldBuildingData.customCategories.indexOf(category), category.items.indexOf(item), 'name', e.target.value)} className={`${inputClass} text-sm`} />
                                                </div>
                                                <div>
                                                    <label htmlFor={`cat-${category.id}-item-desc-${item.id}`} className={`${labelClass} text-xs`}>Description:</label>
                                                    <textarea id={`cat-${category.id}-item-desc-${item.id}`} value={item.description} onChange={(e) => handleCustomItemChange(worldBuildingData.customCategories.indexOf(category), category.items.indexOf(item), 'description', e.target.value)} className={`${textareaClass} min-h-[40px] text-sm`} rows={2} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <button onClick={() => handleAddCustomItem(worldBuildingData.customCategories.indexOf(category))} className={`${secondaryButtonClass} text-xs mt-2 mb-4`} disabled={!hasInitialBlueprint}>Add Item Manually to "{category.categoryName || 'This Category'}"</button>
                                
                                <div className="mt-4 pt-4 border-t border-gray-300 space-y-4">
                                    <div>
                                        <h5 className="text-sm font-semibold text-gray-600 mb-1">Refine/Expand Items in "{category.categoryName}" with AI:</h5>
                                        <textarea 
                                            value={customCategoryAIRefineInputs[category.id] || ''}
                                            onChange={(e) => setCustomCategoryAIRefineInputs(prev => ({...prev, [category.id]: e.target.value}))}
                                            placeholder="Add more details or new ideas for items in this category. AI will use this and existing items to update the list."
                                            className={`${textareaClass} min-h-[60px] text-sm mb-1`}
                                            rows={2}
                                            disabled={!hasInitialBlueprint}
                                        />
                                        <button 
                                            onClick={() => {
                                                handleRefineCustomCategoryItemsWithAI(worldBuildingData.customCategories.indexOf(category), customCategoryAIRefineInputs[category.id] || '');
                                                setCustomCategoryAIRefineInputs(prev => ({...prev, [category.id]: ''}));
                                            }}
                                            disabled={contextIsLoading.refineCustomCategoryItems || !(customCategoryAIRefineInputs[category.id] || '').trim() || !hasInitialBlueprint}
                                            className={`${actionButtonClass} text-xs w-full`}
                                        >
                                            {contextIsLoading.refineCustomCategoryItems ? 'Refining...' : 'Refine/Expand Items (AI)'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button onClick={handleAddCustomCategory} className={`${primaryButtonClass} mt-2`} disabled={!hasInitialBlueprint}>Add New Custom Category</button>
                        <SaveCancelButtons
                            onSave={handleSaveChanges}
                            onCancel={handleCancelEdit}
                        />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {worldBuildingData.customCategories.length > 0 ? (
                            worldBuildingData.customCategories?.map((category, index) => (
                                <div key={category.id} className="space-y-2">
                                    <DisplayField
                                        label={`Category ${index + 1}: ${category.categoryName || 'Unnamed Category'}`}
                                        value={`${category.items.length} items defined`}
                                        background={true}
                                    />
                                    {category.items.length > 0 && (
                                        <div className="ml-4 space-y-1">
                                            {category.items?.map((item, itemIndex) => (
                                                <DisplayField
                                                    key={item.id}
                                                    label={`Item ${itemIndex + 1}: ${item.name || 'Unnamed'}`}
                                                    value={item.description}
                                                    background={false}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <DisplayField
                                label="Custom World Elements"
                                value="No custom categories defined yet"
                                background={true}
                            />
                        )}
                    </div>
                )}
            </ViewEditSection>

            {/* Holistic World Regeneration Section */}
            <section className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 p-6 rounded-lg shadow-xl border-t-4 border-indigo-500">
                <h3 className="text-xl font-semibold text-gray-700 mb-4">Holistic World Regeneration (AI)</h3>
                <p className="text-sm text-gray-600 mb-3">
                    Provide a detailed prompt to revise or completely regenerate ALL aspects of your world-building data (Story Vitals, Characters, Locations, Custom Elements). 
                    The AI will use your prompt, the existing world data as context, and your overall project idea to create a new set of world-building elements.
                </p>
                <p className="text-xs text-yellow-700 bg-yellow-100 p-2 rounded-md mb-3">
                    <strong>Warning:</strong> This action will replace all current world-building data. Ensure your prompt is comprehensive.
                </p>
                <div>
                    <label htmlFor="overallRevisionPrompt" className={`${labelClass} font-medium`}>Overall Regeneration/Revision Prompt:</label>
                    <textarea
                        id="overallRevisionPrompt"
                        value={overallRevisionPrompt}
                        onChange={(e) => setOverallRevisionPrompt(e.target.value)}
                        placeholder="e.g., 'Transform my fantasy world into a dark sci-fi setting. Focus on cybernetics, rogue AIs, and megacorporations. Keep the core theme of rebellion, but update characters and locations accordingly.' or 'Reimagine the magic system to be based on ancient runes and elemental spirits. Update characters to reflect these changes.'"
                        className={`${textareaClass} min-h-[120px]`}
                        rows={5}
                        aria-describedby="overallRevisionHelp"
                        disabled={!hasInitialBlueprint}
                    />
                    <p id="overallRevisionHelp" className="text-xs text-gray-500 mt-1">
                        Be specific about what you want to change, keep, or introduce across all world elements.
                    </p>
                </div>
                <button
                    onClick={triggerRegenerateAll}
                    disabled={
                        (hasInitialBlueprint && !allowBlueprintRegeneration) || // Disable if blueprint exists and checkbox is not checked
                        (contextIsLoading.regenerateAllWorldBuilding === true) || 
                        !overallRevisionPrompt.trim()
                    }
                    className={`${actionButtonClass} w-full mt-4 py-2.5 text-base font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300`}
                >
                    {contextIsLoading.regenerateAllWorldBuilding ? 'Regenerating All World Data...' : 'Regenerate All World Data with AI'}
                </button>
            </section>
            
            <p className="text-xs text-gray-500 mt-6 text-center">
                All changes made here are part of your current project. 
                Remember to "Save Project" from the top menu to persist them permanently. 
                This detailed world information will be used by the AI when generating and revising chapters.
            </p>
        </div>
    );
};

export default WorldBuildingView;